import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const FILTER_RESOURCE_KHTG_LEISTUNGSGRUPPE = 'khtg-leistungsgruppe';

export const KHTG_LEISTUNGSGRUPPE_CONTENT_CONDITION_ID = 'khtgLeistungsgruppe';
export const FilterConditionKhtgLeistungsgruppePluginType: FilterConditionPluginSpecification<
  string[],
  FilterConditionKhtgLeistungsgruppeDto,
  KhtgLeistungsgruppeContentCondition
> = {
  type: 'khtgLeistungsgruppe',
  category: 'case',
  displayName: { de: 'KHTG Leistungsgruppen' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionKhtgLeistungsgruppeDto,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionKhtgLeistungsgruppePlugin(obj.leistungsgruppen);
  },
  fromCondition(
    jsonObj: KhtgLeistungsgruppeContentCondition,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionKhtgLeistungsgruppePlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<string[]> {
    return new FilterConditionKhtgLeistungsgruppePlugin([]);
  },
};

export class FilterConditionKhtgLeistungsgruppePlugin extends FilterConditionPluginImpl<
  string[],
  FilterConditionKhtgLeistungsgruppeDto
> {
  static override Specification: FilterConditionPluginSpecification<
    string[],
    FilterConditionKhtgLeistungsgruppeDto,
    KhtgLeistungsgruppeContentCondition
  > = FilterConditionKhtgLeistungsgruppePluginType;

  constructor(private leistungsgruppen: string[]) {
    super(FilterConditionKhtgLeistungsgruppePluginType.type);
  }

  clone(): FilterConditionKhtgLeistungsgruppePlugin {
    return new FilterConditionKhtgLeistungsgruppePlugin([
      ...this.leistungsgruppen,
    ]);
  }

  override equals(other: FilterConditionKhtgLeistungsgruppePlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.leistungsgruppen, other.leistungsgruppen)
    );
  }

  override validationToText(): string {
    return this.valid() ? '' : 'Mindestens eine KHTG Leistungsgruppe auswählen';
  }

  toCondition(): BdCondition | null {
    return new KhtgLeistungsgruppeContentCondition(this.leistungsgruppen, []);
  }

  toText(): string | null {
    return this.leistungsgruppen.length === 1
      ? 'eine KHTG Leistungsgruppe'
      : `${this.leistungsgruppen.length} KHTG Leistungsgruppen`;
  }

  override getExtraChildResources(): Record<string, string[]> {
    if (this.leistungsgruppen.length === 0) return {};
    return { [FILTER_RESOURCE_KHTG_LEISTUNGSGRUPPE]: [...this.leistungsgruppen] };
  }

  override toDetailedText(
    resolvedNames?: Map<string, string>,
  ): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.leistungsgruppen.map((key) => {
      const name = resolvedNames?.get(key);
      return name ? `${key} — ${name}` : key;
    });
    return { title, items };
  }

  toJSON(): FilterConditionKhtgLeistungsgruppeDto | null {
    return {
      type: this.type,
      leistungsgruppen: this.leistungsgruppen,
    };
  }

  toValue(): string[] {
    return this.leistungsgruppen;
  }
}

export class KhtgLeistungsgruppeContentCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[]) {
    super(KHTG_LEISTUNGSGRUPPE_CONTENT_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionKhtgLeistungsgruppeDto {
  type: string;
  leistungsgruppen: string[];
}
